import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ChatCircle, PaperPlaneTilt, ThumbsUp } from "@phosphor-icons/react";
import { useMutation, useQuery } from "convex/react";
import { Link, Navigate, NavLink, useNavigate, useParams } from "react-router-dom";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type {
  AudienceCategory,
  ParticipantIdentity,
  ParticipantState,
  RoomThread,
} from "../../shared/types.ts";
import { normalizeAudienceError } from "../lib/audienceErrorMessage.ts";
import { buildJoinPath, buildJoinSummaryPath, normalizeJoinCode } from "../lib/join.ts";
import { resolveSessionViewRoute } from "../lib/sessionViewRoute.ts";
import {
  notificationCopy,
  participantJoinedCopy,
  participantMoveNeedsJoinCopy,
  participantNameUpdatedCopy,
  participantSubmissionLiveCopy,
  participantThreadAnsweredCopy,
  sessionMovedCopy,
} from "../lib/notificationCopy.ts";
import { notify } from "../lib/notify.ts";
import {
  clearParticipantHandoffIntent,
  readParticipantHandoffIntent,
} from "../lib/sessionHandoffStorage.ts";
import { fmtAgo, groupByCategory, sortThreads } from "../lib/threads.ts";
import { AudienceRecapPanel } from "../components/AudienceRecapPanel.tsx";
import { EmptyState } from "../components/EmptyState.tsx";
import { RoomThreadCard, SelfAttributionMark } from "../components/RoomThreadCard.tsx";
import { SortControls, type ThreadSort } from "../components/SortControls.tsx";
import { AliasModal } from "./AliasModal.tsx";
import {
  composerIdentityCopy,
  joinWelcomeIntro,
  participantJoinPresentation,
  type JoinWelcomeIntro,
} from "./participantIdentityUi.ts";
import {
  optimisticallyTogglePostLike,
  optimisticallyToggleReplyLike,
} from "./optimisticLikes.ts";
import {
  readLastParticipantAlias,
  readParticipantDrafts,
  writeLastParticipantAlias,
  writeParticipantPostDraft,
  writeParticipantReplyDraft,
} from "./participantSessionStorage.ts";

const STORAGE_PREFIX = "comodo.participant";

/** Brand + session context block atop the first-join gate: the modal welcomes
 *  people to a specific event instead of only asking for a name. When the
 *  session has a title it becomes the dialog's h1 (participant-join-title),
 *  so callers must render their own fallback h1 when `intro.title` is null. */
function JoinPanelWelcome({ intro }: { intro: JoinWelcomeIntro }) {
  return (
    <>
      <div className="join-brand" aria-hidden="true">
        <span className="join-brand-mark" />
        <span className="join-brand-word">COMODO</span>
      </div>
      {intro.title && (
        <div className="join-session-intro">
          <span className="join-kick">Live panel</span>
          <h1 id="participant-join-title" className="join-session-title">{intro.title}</h1>
          {intro.speakers.length > 0 && (
            <div className="join-speakers" data-testid="participant-join-speakers">
              {intro.speakers.map((panelist) => (
                <div className="join-speaker" key={panelist.name}>
                  {panelist.name}
                  {panelist.isModerator && <span className="join-modtag">Moderator</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function ParticipantView() {
  const navigate = useNavigate();
  const { joinCode: routeJoinCode, "*": routeSuffix } = useParams();
  const requestedJoinCode = normalizeJoinCode(routeJoinCode);
  const sessionViewRoute = resolveSessionViewRoute(routeSuffix);
  const [codeInput, setCodeInput] = useState("");
  const [clientKey, setClientKey] = useState<string | null>(null);
  const [alias, setAlias] = useState<string | null>(null);
  const [joinedIdentity, setJoinedIdentity] = useState<ParticipantIdentity | null>(null);
  const [identitySessionId, setIdentitySessionId] = useState<string | null>(null);
  const [aliasInput, setAliasInput] = useState("");
  const [joinSubmitting, setJoinSubmitting] = useState(false);
  const [handoffJoinFailed, setHandoffJoinFailed] = useState(false);
  const [postDraft, setPostDraft] = useState("");
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [postSubmitting, setPostSubmitting] = useState(false);
  const [replySubmittingIds, setReplySubmittingIds] = useState<Set<string>>(() => new Set());
  const [openReply, setOpenReply] = useState<string | null>(null);
  const [sort, setSort] = useState<ThreadSort>("latest-thread");
  const [byCategory, setByCategory] = useState(false);
  const [aliasModalOpen, setAliasModalOpen] = useState(false);
  const [aliasSaving, setAliasSaving] = useState(false);
  const [headerCompact, setHeaderCompact] = useState(false);
  const [toolbarStuck, setToolbarStuck] = useState(false);
  const mainRef = useRef<HTMLElement | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const transitionPrimedRef = useRef(false);
  const postSubmitInFlightRef = useRef(false);
  const replySubmitInFlightRef = useRef<Set<string>>(new Set());
  const pendingPostIdsRef = useRef<Set<string>>(new Set());
  const pendingReplyIdsRef = useRef<Set<string>>(new Set());
  const visiblePostIdsRef = useRef<Set<string>>(new Set());
  const answeredPostIdsRef = useRef<Set<string>>(new Set());
  const privacySignatureRef = useRef<string | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);
  const joinInFlightSessionRef = useRef<string | null>(null);
  const autoJoinAttemptedSessionRef = useRef<string | null>(null);
  const movedNotificationRevisionRef = useRef<number | null>(null);

  // Hysteresis (compact past 48px, expand back under 8px): compacting frees
  // ~15px of scroll range, so a single threshold could oscillate when the
  // content barely overflows. The gap must stay wider than that height delta.
  function handleMainScroll() {
    const main = mainRef.current;
    if (!main) return;
    const top = main.scrollTop;
    setHeaderCompact((compact) => (compact ? top > 8 : top > 48));
    const bar = toolbarRef.current;
    if (bar) {
      setToolbarStuck(bar.getBoundingClientRect().top <= main.getBoundingClientRect().top + 1);
    }
  }

  const joinInfo = useQuery(
    convexApi.audience.resolveJoinCode,
    requestedJoinCode ? { joinCode: requestedJoinCode } : "skip",
  );
  // Pre-validate a typed code before navigating (spec §5.4).
  const normalizedInput = normalizeJoinCode(codeInput);
  const probe = useQuery(
    convexApi.audience.resolveJoinCode,
    !requestedJoinCode && normalizedInput.length === 6 ? { joinCode: normalizedInput } : "skip",
  );
  const sessionId = joinInfo?.sessionId as Id<"sessions"> | undefined;
  currentSessionIdRef.current = sessionId ?? null;
  const storedTargetIdentity = useMemo(() => {
    if (!sessionId) return { clientKey: null, alias: null };
    return {
      clientKey: readStorage(clientKeyStorage(sessionId)),
      alias: readStorage(aliasStorage(sessionId)),
    };
  }, [sessionId]);
  const handoffIntent = useMemo(
    () => sessionId ? readParticipantHandoffIntent(sessionId) : null,
    [sessionId],
  );
  const carriedAlias = useMemo(
    () => handoffIntent ? readLastParticipantAlias() : null,
    [handoffIntent],
  );
  const joinPresentation = useMemo(
    () => joinInfo
      ? participantJoinPresentation(joinInfo.identityMode, joinInfo.publicAuthorDisplay)
      : null,
    [joinInfo?.identityMode, joinInfo?.publicAuthorDisplay],
  );
  // Content loads pre-join: the query returns the public session state with a
  // null participant until a clientKey exists, so the room is visible (blurred)
  // behind the join overlay instead of hidden behind a bare name gate.
  const state = useQuery(
    convexApi.audience.getParticipantState,
    sessionId ? { sessionId, ...(clientKey ? { clientKey } : {}) } : "skip",
  ) as ParticipantState | null | undefined;
  const join = useMutation(convexApi.audience.join);
  const createPost = useMutation(convexApi.audience.createPost);
  const createReply = useMutation(convexApi.audience.createReply);
  const togglePostLike = useMutation(convexApi.audience.togglePostLike)
    .withOptimisticUpdate((localStore, args) => {
      const queryArgs = { sessionId: args.sessionId, clientKey: args.clientKey };
      const current = localStore.getQuery(convexApi.audience.getParticipantState, queryArgs);
      if (!current) return;
      localStore.setQuery(
        convexApi.audience.getParticipantState,
        queryArgs,
        optimisticallyTogglePostLike(current, args.postId),
      );
    });
  const toggleReplyLike = useMutation(convexApi.audience.toggleReplyLike)
    .withOptimisticUpdate((localStore, args) => {
      const queryArgs = { sessionId: args.sessionId, clientKey: args.clientKey };
      const current = localStore.getQuery(convexApi.audience.getParticipantState, queryArgs);
      if (!current) return;
      localStore.setQuery(
        convexApi.audience.getParticipantState,
        queryArgs,
        optimisticallyToggleReplyLike(current, args.replyId),
      );
    });

  const projected = state?.projectedContent?.type === "thread" ? state.projectedContent : null;
  const { liveThreads, answeredThreads } = useMemo(() => {
    const threads = (state?.threads ?? []).filter((t) => t.id !== projected?.thread.id);
    const sorted = sortThreads(threads, sort);
    return {
      liveThreads: sorted.filter((thread) => thread.status === "shown"),
      answeredThreads: sorted.filter((thread) => thread.status === "answered"),
    };
  }, [projected?.thread.id, sort, state?.threads]);
  // Author-scoped pending projections (empty in auto-shown sessions and for
  // unresolved participants); guarded for clients talking to an older backend.
  const pendingThreads = state?.pendingThreads ?? [];
  const pendingReplies = state?.pendingReplies ?? [];
  const pendingCount = pendingThreads.length + pendingReplies.length;

  useEffect(() => {
    if (!sessionId) {
      setClientKey(null);
      setAlias(null);
      setAliasInput("");
      setJoinedIdentity(null);
      setIdentitySessionId(null);
      setPostDraft("");
      setReplyDrafts({});
      return;
    }

    const drafts = readParticipantDrafts(sessionId);
    setClientKey(storedTargetIdentity.clientKey);
    setAlias(storedTargetIdentity.alias);
    setAliasInput(storedTargetIdentity.alias ?? carriedAlias ?? "");
    setJoinedIdentity(null);
    setIdentitySessionId(sessionId);
    setJoinSubmitting(false);
    setHandoffJoinFailed(false);
    setPostDraft(drafts.post);
    setReplyDrafts(drafts.replies);
    setOpenReply(null);
    privacySignatureRef.current = null;
  }, [carriedAlias, sessionId, storedTargetIdentity]);

  useEffect(() => {
    transitionPrimedRef.current = false;
    pendingPostIdsRef.current = new Set();
    pendingReplyIdsRef.current = new Set();
    visiblePostIdsRef.current = new Set();
    answeredPostIdsRef.current = new Set();
  }, [sessionId, clientKey]);

  useEffect(() => {
    if (!state?.participantKey) return;

    const pendingPostIds = new Set(state.pendingThreads.map((thread) => thread.id));
    const pendingReplyIds = new Set(state.pendingReplies.map(({ reply }) => reply.id));
    const visiblePostIds = ownVisiblePostIds(state);
    const visibleReplyIds = ownVisibleReplyIds(state);
    const answeredPostIds = ownAnsweredPostIds(state);

    if (transitionPrimedRef.current) {
      const livePostId = firstMovedFromPendingToVisible(pendingPostIdsRef.current, pendingPostIds, visiblePostIds);
      const liveReplyId = firstMovedFromPendingToVisible(pendingReplyIdsRef.current, pendingReplyIds, visibleReplyIds);
      const liveBody = livePostId ? threadBody(state, livePostId) : liveReplyId ? replyBody(state, liveReplyId) : undefined;
      if (liveBody) notify.success(participantSubmissionLiveCopy(liveBody));
      else if (livePostId || liveReplyId) notify.success(notificationCopy.participant.nowLive);

      const answeredThreadId = Array.from(answeredPostIds).find(
        (id) =>
          !answeredPostIdsRef.current.has(id) &&
          visiblePostIdsRef.current.has(id) &&
          !pendingPostIdsRef.current.has(id),
      );
      if (answeredThreadId) {
        const body = threadBody(state, answeredThreadId);
        notify.info(body ? participantThreadAnsweredCopy(body) : notificationCopy.participant.answered);
      }
    }

    transitionPrimedRef.current = true;
    pendingPostIdsRef.current = pendingPostIds;
    pendingReplyIdsRef.current = pendingReplyIds;
    visiblePostIdsRef.current = visiblePostIds;
    answeredPostIdsRef.current = answeredPostIds;
  }, [state]);

  useEffect(() => {
    if (state?.identity && !state.identity.aliasEditable) setAliasModalOpen(false);
  }, [state?.identity]);

  useEffect(() => {
    if (!state?.identity) return;
    const signature = `${state.policy.identityMode}:${state.policy.publicAuthorDisplay}`;
    const previous = privacySignatureRef.current;
    privacySignatureRef.current = signature;
    if (!previous || previous === signature) return;
    notify.info({
      title: "Session privacy updated.",
      description: composerIdentityCopy(state.identity, state.policy.publicAuthorDisplay),
    });
  }, [state?.identity, state?.policy.identityMode, state?.policy.publicAuthorDisplay]);

  // Inline validation: a fully-typed code resolves before we navigate (spec §5.4).
  const codeInvalid = normalizedInput.length === 6 && probe === null;
  const [pendingSubmit, setPendingSubmit] = useState(false);

  useEffect(() => {
    if (!pendingSubmit) return;
    if (normalizedInput.length !== 6) {
      setPendingSubmit(false);
      return;
    }
    if (probe === undefined) return; // still validating — navigate once resolved
    setPendingSubmit(false);
    if (probe) navigate(buildJoinPath(normalizedInput));
  }, [navigate, normalizedInput, pendingSubmit, probe]);

  function submitCode(event: FormEvent) {
    event.preventDefault();
    const nextCode = normalizeJoinCode(codeInput);
    if (!nextCode) return;
    if (nextCode.length === 6) {
      setPendingSubmit(true); // validated (or rejected inline) when the probe resolves
      return;
    }
    navigate(buildJoinPath(nextCode));
  }

  async function submitJoin(event: FormEvent) {
    event.preventDefault();
    if (!sessionId || !joinPresentation) return;
    if (joinInFlightSessionRef.current === sessionId) return;
    const joiningSessionId = sessionId;
    const nextAlias = aliasInput.trim();
    if (joinPresentation.requiresAlias && !nextAlias) return;
    const key = identitySessionId === joiningSessionId && clientKey
      ? clientKey
      : createClientKey();
    joinInFlightSessionRef.current = joiningSessionId;
    setJoinSubmitting(true);
    try {
      const result = await join({
        sessionId: joiningSessionId,
        clientKey: key,
        ...(joinPresentation.requiresAlias ? { alias: nextAlias } : {}),
      });
      writeStorage(clientKeyStorage(joiningSessionId), key);
      if (joinPresentation.requiresAlias) {
        writeStorage(aliasStorage(joiningSessionId), result.participant.alias);
        writeLastParticipantAlias(result.participant.alias);
      } else {
        removeStorage(aliasStorage(joiningSessionId));
      }
      clearParticipantHandoffIntent(joiningSessionId);
      if (currentSessionIdRef.current === joiningSessionId) {
        setClientKey(key);
        setAlias(joinPresentation.requiresAlias ? result.participant.alias : null);
        setJoinedIdentity(result.identity);
        notify.success(participantJoinedCopy(result.session.sessionTitle, result.identity.label));
      }
    } catch (error) {
      if (currentSessionIdRef.current === joiningSessionId) {
        notify.error(normalizeAudienceError(error, "join"));
      }
    } finally {
      if (joinInFlightSessionRef.current === joiningSessionId) {
        joinInFlightSessionRef.current = null;
      }
      if (currentSessionIdRef.current === joiningSessionId) setJoinSubmitting(false);
    }
  }

  async function submitPost(event: FormEvent) {
    event.preventDefault();
    if (postSubmitInFlightRef.current) return;
    if (!postDraft.trim()) return;
    if (!sessionId || !clientKey) {
      notify.error(notificationCopy.participant.joinBeforePosting);
      return;
    }
    const postingSessionId = sessionId;
    postSubmitInFlightRef.current = true;
    setPostSubmitting(true);
    try {
      await createPost({ sessionId: postingSessionId, clientKey, body: postDraft });
      writeParticipantPostDraft(postingSessionId, "");
      if (currentSessionIdRef.current === postingSessionId) {
        setPostDraft("");
        notify.success(
          state?.policy.postVisibilityMode === "auto-shown"
            ? notificationCopy.participant.posted
            : notificationCopy.participant.sentForReview,
        );
      }
    } catch (error) {
      if (currentSessionIdRef.current === postingSessionId) {
        notify.error(normalizeAudienceError(error, "post"));
      }
    } finally {
      postSubmitInFlightRef.current = false;
      if (currentSessionIdRef.current === postingSessionId) setPostSubmitting(false);
    }
  }

  async function submitReply(thread: RoomThread) {
    if (replySubmitInFlightRef.current.has(thread.id)) return;
    const draft = replyDrafts[thread.id]?.trim();
    if (!draft) return;
    if (!sessionId || !clientKey) {
      notify.error(notificationCopy.participant.joinBeforePosting);
      return;
    }
    const replyingSessionId = sessionId;
    replySubmitInFlightRef.current.add(thread.id);
    setReplySubmittingIds((current) => new Set(current).add(thread.id));
    try {
      await createReply({ sessionId: replyingSessionId, clientKey, postId: thread.id as Id<"audiencePosts">, body: draft });
      writeParticipantReplyDraft(replyingSessionId, thread.id, "");
      if (currentSessionIdRef.current === replyingSessionId) {
        setReplyDrafts((current) => ({ ...current, [thread.id]: "" }));
        setOpenReply(null);
        notify.success(
          state?.policy.replyVisibilityMode === "auto-shown"
            ? notificationCopy.participant.replyPosted
            : notificationCopy.participant.replySentForReview,
        );
      }
    } catch (error) {
      if (currentSessionIdRef.current === replyingSessionId) {
        notify.error(normalizeAudienceError(error, "reply"));
      }
    } finally {
      replySubmitInFlightRef.current.delete(thread.id);
      if (currentSessionIdRef.current === replyingSessionId) {
        setReplySubmittingIds((current) => {
          const next = new Set(current);
          next.delete(thread.id);
          return next;
        });
      }
    }
  }

  async function saveAlias(newAlias: string) {
    if (!sessionId || !clientKey) {
      notify.error(notificationCopy.participant.joinBeforePosting);
      return;
    }
    setAliasSaving(true);
    try {
      const result = await join({ sessionId, alias: newAlias, clientKey });
      writeStorage(aliasStorage(sessionId), result.participant.alias);
      writeLastParticipantAlias(result.participant.alias);
      setAliasInput(result.participant.alias);
      setAlias(result.participant.alias);
      setJoinedIdentity(result.identity);
      setAliasModalOpen(false);
      notify.success(participantNameUpdatedCopy(result.participant.alias));
    } catch (error) {
      notify.error(normalizeAudienceError(error, "alias"));
    } finally {
      setAliasSaving(false);
    }
  }

  function likeThread(thread: RoomThread) {
    if (!sessionId || !clientKey) return;
    void togglePostLike({ sessionId, clientKey, postId: thread.id as Id<"audiencePosts"> })
      .catch((error) => notify.error(normalizeAudienceError(error, "like")));
  }

  function likeReply(replyId: string) {
    if (!sessionId || !clientKey) return;
    void toggleReplyLike({ sessionId, clientKey, replyId: replyId as Id<"audienceReplies"> })
      .catch((error) => notify.error(normalizeAudienceError(error, "like")));
  }

  const joined = Boolean(
    sessionId &&
    identitySessionId === sessionId &&
    clientKey &&
    (alias || joinInfo?.identityMode === "fully_anonymous"),
  );
  const effectiveIdentity = state?.identity ?? joinedIdentity;
  const hasStoredTargetIdentity = Boolean(
    storedTargetIdentity.clientKey &&
    (storedTargetIdentity.alias || joinInfo?.identityMode === "fully_anonymous"),
  );
  const canAutomaticallyJoin = Boolean(
    sessionId &&
    joinInfo?.participantJoinOpen &&
    !joined &&
    !handoffJoinFailed &&
    (
      joinInfo.identityMode === "fully_anonymous" ||
      (handoffIntent && joinPresentation?.requiresAlias && carriedAlias)
    ),
  );
  const showAutomaticJoinProgress = Boolean(
    !joined &&
    !handoffJoinFailed &&
    (
      (handoffIntent && hasStoredTargetIdentity) ||
      canAutomaticallyJoin
    ),
  );

  // A handoff creates ordinary target-session membership. Named sessions use
  // the last successful alias with a fresh key; fully anonymous sessions keep
  // their existing silent-join behavior.
  useEffect(() => {
    if (!sessionId || !joinInfo || !joinPresentation || joined) return;
    if (identitySessionId !== sessionId || !canAutomaticallyJoin) return;
    if (autoJoinAttemptedSessionRef.current === sessionId) return;
    if (joinInFlightSessionRef.current === sessionId) return;

    const joiningSessionId = sessionId;
    const joiningIntent = handoffIntent;
    const nextAlias = joiningIntent && joinPresentation.requiresAlias ? carriedAlias : null;
    const key = joiningIntent ? createClientKey() : clientKey || createClientKey();
    autoJoinAttemptedSessionRef.current = joiningSessionId;
    joinInFlightSessionRef.current = joiningSessionId;
    setJoinSubmitting(true);

    void join({
      sessionId: joiningSessionId,
      clientKey: key,
      ...(nextAlias ? { alias: nextAlias } : {}),
    }).then((result) => {
      writeStorage(clientKeyStorage(joiningSessionId), key);
      if (joinPresentation.requiresAlias) {
        writeStorage(aliasStorage(joiningSessionId), result.participant.alias);
        writeLastParticipantAlias(result.participant.alias);
      } else {
        removeStorage(aliasStorage(joiningSessionId));
      }
      clearParticipantHandoffIntent(joiningSessionId);
      if (currentSessionIdRef.current !== joiningSessionId) return;
      setClientKey(key);
      setAlias(joinPresentation.requiresAlias ? result.participant.alias : null);
      setJoinedIdentity(result.identity);
      if (
        joiningIntent &&
        movedNotificationRevisionRef.current !== joiningIntent.revision
      ) {
        movedNotificationRevisionRef.current = joiningIntent.revision;
        notify.success(sessionMovedCopy(result.session.sessionTitle));
      }
    }).catch((error) => {
      clearParticipantHandoffIntent(joiningSessionId);
      if (currentSessionIdRef.current !== joiningSessionId) return;
      setHandoffJoinFailed(true);
      if (joiningIntent) {
        movedNotificationRevisionRef.current = joiningIntent.revision;
        const normalized = normalizeAudienceError(error, "join");
        notify.error(participantMoveNeedsJoinCopy(joinInfo.sessionTitle, normalized.title));
      }
    }).finally(() => {
      if (joinInFlightSessionRef.current === joiningSessionId) {
        joinInFlightSessionRef.current = null;
      }
      if (currentSessionIdRef.current === joiningSessionId) setJoinSubmitting(false);
    });
  }, [
    canAutomaticallyJoin,
    carriedAlias,
    clientKey,
    handoffIntent,
    identitySessionId,
    join,
    joined,
    joinInfo,
    joinPresentation,
    sessionId,
  ]);

  // Existing target membership wins. If automatic continuation is impossible,
  // acknowledge the move and leave the ordinary join form available.
  useEffect(() => {
    if (!handoffIntent || !joinInfo || !sessionId) return;
    if (identitySessionId !== sessionId) return;
    if (!joined && canAutomaticallyJoin) return;
    if (!joined && hasStoredTargetIdentity) return;
    clearParticipantHandoffIntent(sessionId);
    if (movedNotificationRevisionRef.current === handoffIntent.revision) return;
    movedNotificationRevisionRef.current = handoffIntent.revision;
    notify.success(sessionMovedCopy(joinInfo.sessionTitle));
  }, [
    canAutomaticallyJoin,
    handoffIntent,
    hasStoredTargetIdentity,
    identitySessionId,
    joined,
    joinInfo,
    sessionId,
  ]);

  function updatePostDraft(value: string) {
    setPostDraft(value);
    if (sessionId) writeParticipantPostDraft(sessionId, value);
  }

  function updateReplyDraft(threadId: string, value: string) {
    setReplyDrafts((current) => ({ ...current, [threadId]: value }));
    if (sessionId) writeParticipantReplyDraft(sessionId, threadId, value);
  }

  const renderCard = (thread: RoomThread, hero = false, showReplies = true) => (
    <PtCard
      key={thread.id}
      thread={thread}
      ownMark={Boolean(state?.participantKey && thread.authorKey === state.participantKey)}
      participantKey={state?.participantKey ?? null}
      hero={hero}
      showReplies={showReplies}
      categories={state?.categories ?? []}
      repliesEnabled={Boolean(state?.policy.repliesEnabled)}
      replyOpen={openReply === thread.id}
      replyDraft={replyDrafts[thread.id] ?? ""}
      replySubmitting={replySubmittingIds.has(thread.id)}
      onReplyToggle={() => setOpenReply((cur) => (cur === thread.id ? null : thread.id))}
      onReplyDraft={(v) => updateReplyDraft(thread.id, v)}
      onReplySubmit={() => void submitReply(thread)}
      onLike={() => likeThread(thread)}
      onReplyLike={likeReply}
      identityHint={state?.identity ? composerIdentityCopy(state.identity, state.policy.publicAuthorDisplay) : null}
    />
  );

  const renderThreadCollection = (threads: RoomThread[], keyPrefix: string) => byCategory
    ? groupByCategory(threads, state?.categories ?? []).map((group) => (
        <section
          className="participant-category-section"
          key={`${keyPrefix}-${group.category?.id ?? "none"}`}
        >
          <h3>{group.category?.label ?? "Other"} <span className="count">{group.threads.length}</span></h3>
          <div className="participant-thread-stack">
            {group.threads.map((thread) => renderCard(thread))}
          </div>
        </section>
      ))
    : <div className="participant-thread-stack">{threads.map((thread) => renderCard(thread))}</div>;

  if (!requestedJoinCode) {
    return (
      <div className="participant-page">
        <header className="participant-header">
          <div>
            <strong>COMODO</strong>
            <span>Audience</span>
          </div>
        </header>
        <main className="participant-join">
          <form className="panel join-panel" data-testid="join-code-entry" onSubmit={submitCode}>
            <h1>Join the discussion</h1>
            <p>Enter the 6-character code shown on the room screen.</p>
            <input
              name="joinCode"
              aria-label="Join code"
              data-testid="join-code-input"
              value={codeInput}
              onChange={(event) => setCodeInput(normalizeJoinCode(event.target.value))}
              placeholder="ABC123"
              maxLength={6}
              style={{ fontFamily: "var(--mono)", fontSize: 18, letterSpacing: "0.14em", textAlign: "center" }}
            />
            {codeInvalid && (
              <div className="field-error" data-testid="join-code-error" role="alert">
                ✕ Code not found — check the screen or ask the host.
              </div>
            )}
            <div className="field-hint">Letters and numbers only; we uppercase it for you.</div>
            <button className="btn primary big" data-testid="join-code-submit" disabled={!normalizeJoinCode(codeInput)}>
              Continue
            </button>
          </form>
        </main>
      </div>
    );
  }

  const participantBasePath = buildJoinPath(requestedJoinCode);
  const timelinePath = buildJoinSummaryPath(requestedJoinCode, "timeline");
  const consolidatedPath = buildJoinSummaryPath(requestedJoinCode, "consolidated");
  const themesPath = buildJoinSummaryPath(requestedJoinCode, "themes");
  if (sessionViewRoute.kind === "redirect") {
    return (
      <Navigate
        to={sessionViewRoute.destination === "timeline" ? timelinePath : participantBasePath}
        replace
      />
    );
  }
  const showingSummaries = sessionViewRoute.kind === "summary";
  const summaryPage = sessionViewRoute.kind === "summary" ? sessionViewRoute.page : "timeline";

  if (joinInfo === undefined) {
    return <div className="participant-page"><div className="loading">Loading session…</div></div>;
  }

  if (!joinInfo) {
    return (
      <div className="participant-page">
        <header className="participant-header">
          <div>
            <strong>COMODO</strong>
            <span>Audience</span>
          </div>
        </header>
        <main className="participant-join">
          <section className="panel join-panel" data-testid="participant-unavailable">
            <h1>Session not found</h1>
            <p>Check the join code on the room screen.</p>
            <button className="btn ghost big" onClick={() => navigate("/participate")}>Enter another code</button>
          </section>
        </main>
      </div>
    );
  }

  const resolvedJoinPresentation = joinPresentation
    ?? participantJoinPresentation(joinInfo.identityMode, joinInfo.publicAuthorDisplay);
  const joinWelcome = joinWelcomeIntro(joinInfo.sessionTitle, joinInfo.panelists);

  return (
    <div className={`participant-page${joined ? " participant-page-live" : ""}`}>
      <header className={`topbar participant-header${joined && headerCompact ? " is-compact" : ""}`}>
        <div className="participant-header-top">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true" />
            <h1 className="participant-session-title" data-testid="participant-session-title">
              {joinInfo.sessionTitle ?? state?.sessionTitle ?? "Audience"}
            </h1>
          </div>
          {joined && effectiveIdentity && (effectiveIdentity.aliasEditable ? (
            <button
              className="alias-chip participant-identity-chip"
              data-testid="participant-change-alias"
              onClick={() => setAliasModalOpen(true)}
              title="Change the name saved for this session"
            >
              <span>{effectiveIdentity.label}</span>
              {effectiveIdentity.kind === "pseudonym" && <small>Generated identity</small>}
            </button>
          ) : (
            <div
              className="alias-chip participant-identity-chip is-readonly"
              data-testid="participant-identity"
              aria-label={`Your generated identity is ${effectiveIdentity.label}`}
            >
              <span>{effectiveIdentity.label}</span>
              <small>Anonymous identity</small>
            </div>
          ))}
        </div>
      </header>

      {!joined && (
        <div className="participant-join-overlay" role="dialog" aria-modal="true" aria-labelledby="participant-join-title">
          {showAutomaticJoinProgress ? (
            <section className="panel join-panel" data-testid="participant-handoff-progress" aria-live="polite">
              <JoinPanelWelcome intro={joinWelcome} />
              {joinWelcome.title ? (
                <h2 className="join-progress-heading">Moving to this session…</h2>
              ) : (
                <h1 id="participant-join-title">Moving to this session…</h1>
              )}
              <p>
                {resolvedJoinPresentation.requiresAlias
                  ? "Keeping your participant name and joining the discussion."
                  : "Joining the discussion."}
              </p>
            </section>
          ) : (
            <form
              className="panel join-panel"
              data-testid="participant-join-form"
              onSubmit={submitJoin}
              aria-busy={joinSubmitting}
            >
              <JoinPanelWelcome intro={joinWelcome} />
              {!joinWelcome.title && <h1 id="participant-join-title">Join the Session</h1>}
              <p>{joinInfo.participantJoinOpen ? resolvedJoinPresentation.description : "Audience joining is currently closed."}</p>
              {resolvedJoinPresentation.requiresAlias && (
                <label className="participant-join-field" htmlFor="participant-alias">
                  <span>{resolvedJoinPresentation.fieldLabel}</span>
                  <input
                    id="participant-alias"
                    name="alias"
                    data-testid="participant-alias"
                    value={aliasInput}
                    onChange={(event) => setAliasInput(event.target.value)}
                    placeholder="e.g. Jordan"
                    maxLength={50}
                    autoComplete="name"
                    autoFocus
                  />
                </label>
              )}
              <button
                className="btn primary big"
                data-testid="participant-join-submit"
                disabled={
                  joinSubmitting ||
                  !joinInfo.participantJoinOpen ||
                  (resolvedJoinPresentation.requiresAlias && !aliasInput.trim())
                }
              >
                {joinSubmitting ? "Joining…" : resolvedJoinPresentation.submitLabel}
              </button>
            </form>
          )}
        </div>
      )}

      {(
        <div className={`participant-live-shell${joined ? "" : " is-gated"}`} aria-hidden={!joined || undefined}>
          {state === undefined ? (
            <main className="participant-main participant-main-loading">
              <div className="loading">Loading discussion…</div>
            </main>
          ) : state === null ? (
            <main className="participant-main participant-main-loading">
              <EmptyState>No active session.</EmptyState>
            </main>
          ) : (
            <>
              <main
                className="participant-main"
                data-testid="participant-main"
                ref={mainRef}
                onScroll={handleMainScroll}
              >
                {projected && (
                  <section data-testid="participant-on-screen">
                    {renderCard(projected.thread, true, projected.showReplies)}
                  </section>
                )}

                {pendingCount > 0 && (
                  <section
                    className="participant-pending-section"
                    data-testid="participant-pending-section"
                    aria-label="Your submissions pending review"
                  >
                    <h3 className="participant-pending-kicker">
                      Pending review <span className="count">{pendingCount}</span>
                    </h3>
                    <div className="participant-pending-stack">
                      {pendingThreads.map((thread) => (
                        <div data-testid="participant-pending-item" data-pending-kind="post" key={thread.id}>
                          <RoomThreadCard
                            thread={thread}
                            categories={state.categories}
                            ownMark
                            ownParticipantKey={state.participantKey}
                            showReplies={false}
                          />
                        </div>
                      ))}
                      {pendingReplies.map(({ reply, parentThread }) => (
                        <div data-testid="participant-pending-item" data-pending-kind="reply" key={reply.id}>
                          <article className="pt-card pt-pending-reply is-own" data-status={reply.status}>
                            <div className="pt-meta">
                              {reply.authorIdentityKind !== "hidden" && reply.authorLabel && (
                                <span className={`who ${reply.authorIdentityKind === "pseudonym" ? "anon" : ""}`}>
                                  {reply.authorLabel}
                                </span>
                              )}
                              <SelfAttributionMark />
                              <span className="dot-sep">·</span>
                              <span className="badge status status-queued">pending</span>
                              <span className="when">{fmtAgo(reply.createdAt)}</span>
                            </div>
                            <div className="pt-pending-reply-context">
                              Replying to “{snippet(parentThread.body)}”
                            </div>
                            <div className="pt-body room-thread-body">{reply.body}</div>
                          </article>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                <div
                  className={`participant-toolbar${toolbarStuck ? " is-stuck" : ""}`}
                  data-testid="participant-toolbar"
                  ref={toolbarRef}
                >
                  <nav className="segmented participant-view-switch" aria-label="Participant pages">
                    <Link
                      to={participantBasePath}
                      aria-current={!showingSummaries ? "page" : undefined}
                      className={!showingSummaries ? "is-active" : ""}
                      data-testid="participant-posts-toggle"
                    >
                      Posts
                    </Link>
                    <Link
                      to={timelinePath}
                      aria-current={showingSummaries ? "page" : undefined}
                      className={showingSummaries ? "is-active" : ""}
                      data-testid="participant-summaries-toggle"
                    >
                      Summaries
                    </Link>
                  </nav>
                  {!showingSummaries ? (
                    <div className="participant-toolbar-controls">
                      <SortControls
                        sort={sort}
                        onSort={setSort}
                        byCategory={byCategory}
                        onByCategory={setByCategory}
                        postNoun="post"
                      />
                    </div>
                  ) : (
                    <div className="participant-toolbar-controls">
                      <div className="select-field participant-summary-view">
                        <span className="select-field-label" aria-hidden="true">View</span>
                        <nav className="segmented secondary oval" aria-label="Summary pages">
                          <NavLink
                            to={timelinePath}
                            onClick={(event) => {
                              if (summaryPage === "timeline") event.preventDefault();
                            }}
                          >
                            Timeline
                          </NavLink>
                          <NavLink
                            to={consolidatedPath}
                            onClick={(event) => {
                              if (summaryPage === "consolidated") event.preventDefault();
                            }}
                          >
                            Consolidated
                          </NavLink>
                          <NavLink
                            to={themesPath}
                            onClick={(event) => {
                              if (summaryPage === "themes") event.preventDefault();
                            }}
                          >
                            Themes
                          </NavLink>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>

                {showingSummaries ? (
                  <AudienceRecapPanel
                    tab={summaryPage}
                    hideTabs
                    sessionId={sessionId!}
                    recaps={state.recaps}
                    flow
                  />
                ) : (
                  <>
                    {liveThreads.length === 0 && answeredThreads.length === 0 && !projected && (
                      <EmptyState size="sm" testid="participant-feed-empty">No posts yet.</EmptyState>
                    )}
                    {renderThreadCollection(liveThreads, "live")}
                    <section className="thread-status-section" data-testid="participant-answered-section">
                      <div className="thread-section-heading">
                        <span>Answered</span>
                        <span className="count">{answeredThreads.length}</span>
                      </div>
                      {answeredThreads.length === 0
                        ? <EmptyState size="sm">No answered posts yet.</EmptyState>
                        : renderThreadCollection(answeredThreads, "answered")}
                    </section>
                  </>
                )}
              </main>

              <div className="participant-composer-shell">
                {state.identity && (
                  <div className="participant-identity-hint" data-testid="participant-identity-hint">
                    {composerIdentityCopy(state.identity, state.policy.publicAuthorDisplay)}
                  </div>
                )}
                <ComposerDock
                  value={postDraft}
                  onChange={updatePostDraft}
                  placeholder={state.policy.postingOpen ? "Ask a question…" : "Posting is closed"}
                  disabled={!state.policy.postingOpen || postSubmitting}
                  submitting={postSubmitting}
                  canSubmit={Boolean(state.policy.postingOpen && !postSubmitting && postDraft.trim())}
                  onSubmit={submitPost}
                />
              </div>
            </>
          )}

          {effectiveIdentity?.aliasEditable && (
            <AliasModal
              open={aliasModalOpen}
              initialAlias={alias ?? ""}
              saving={aliasSaving}
              onClose={() => setAliasModalOpen(false)}
              onSave={saveAlias}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ownVisiblePostIds(state: ParticipantState): Set<string> {
  const participantKey = state.participantKey;
  if (!participantKey) return new Set();
  return new Set(
    state.threads
      .filter((thread) => thread.authorKey === participantKey && thread.status !== "queued")
      .map((thread) => thread.id),
  );
}

function ownVisibleReplyIds(state: ParticipantState): Set<string> {
  const participantKey = state.participantKey;
  if (!participantKey) return new Set();
  return new Set(
    state.threads.flatMap((thread) =>
      (thread.replies ?? [])
        .filter((reply) => reply.authorKey === participantKey && reply.status === "shown")
        .map((reply) => reply.id),
    ),
  );
}

function ownAnsweredPostIds(state: ParticipantState): Set<string> {
  const participantKey = state.participantKey;
  if (!participantKey) return new Set();
  return new Set(
    state.threads
      .filter((thread) => thread.authorKey === participantKey && thread.status === "answered")
      .map((thread) => thread.id),
  );
}

function firstMovedFromPendingToVisible(
  previousPendingIds: Set<string>,
  currentPendingIds: Set<string>,
  currentVisibleIds: Set<string>,
): string | undefined {
  return Array.from(previousPendingIds).find(
    (id) => !currentPendingIds.has(id) && currentVisibleIds.has(id),
  );
}

function threadBody(state: ParticipantState, threadId: string): string | undefined {
  return state.threads.find((thread) => thread.id === threadId)?.body;
}

function replyBody(state: ParticipantState, replyId: string): string | undefined {
  for (const thread of state.threads) {
    const reply = (thread.replies ?? []).find((entry) => entry.id === replyId);
    if (reply) return reply.body;
  }
  return undefined;
}

/**
 * One popover for both settings. Selections apply immediately but the surface
 * closes only on explicit dismiss (Done, outside tap, Esc), so both settings
 * can change in one pass.
 */
function PtCard({
  thread,
  ownMark,
  participantKey,
  hero,
  showReplies,
  categories,
  repliesEnabled,
  replyOpen,
  replyDraft,
  replySubmitting,
  onReplyToggle,
  onReplyDraft,
  onReplySubmit,
  onLike,
  onReplyLike,
  identityHint = null,
}: {
  thread: RoomThread;
  ownMark: boolean;
  participantKey: string | null;
  hero: boolean;
  showReplies: boolean;
  categories: AudienceCategory[];
  repliesEnabled: boolean;
  replyOpen: boolean;
  replyDraft: string;
  replySubmitting: boolean;
  onReplyToggle: () => void;
  onReplyDraft: (v: string) => void;
  onReplySubmit: () => void;
  onLike: () => void;
  onReplyLike: (replyId: string) => void;
  identityHint?: string | null;
}) {
  const replies = showReplies ? (thread.replies ?? []).filter((r) => r.status === "shown") : [];
  const canReply = repliesEnabled && thread.status !== "answered";
  return (
    <RoomThreadCard
      thread={thread}
      categories={categories}
      ownMark={ownMark}
      ownParticipantKey={participantKey}
      hero={hero}
      showReplies={showReplies}
      replies={replies}
      footerRight={
        <span className="pt-engage">
          {canReply && (
            <button type="button" className="txt-btn accent" data-testid="thread-reply-toggle" onClick={onReplyToggle}>
              <ChatCircle weight="bold" /> Reply
            </button>
          )}
          <button
            type="button"
            className={`txt-btn like-btn ${thread.likeCount > 0 ? "has-likes" : ""} ${thread.likedByParticipant ? "liked" : ""}`}
            data-testid="thread-like-btn"
            aria-pressed={Boolean(thread.likedByParticipant)}
            onClick={onLike}
          >
            <ThumbsUp weight={thread.likedByParticipant ? "fill" : "bold"} /> {thread.likeCount}
          </button>
        </span>
      }
      replyComposer={replyOpen && canReply && (
        <>
          {identityHint && (
            <div className="participant-identity-hint participant-reply-identity-hint" data-testid="participant-reply-identity-hint">
              {identityHint}
            </div>
          )}
          <div className="row" style={{ marginTop: 8, marginBottom: 0 }} aria-busy={replySubmitting}>
          <input
            style={{ flex: 1 }}
            name="reply"
            aria-label="Reply"
            value={replyDraft}
            placeholder="Write a reply…"
            data-testid="thread-reply-input"
            disabled={replySubmitting}
            onChange={(e) => onReplyDraft(e.target.value)}
            onKeyDown={(e) => {
              if (!replySubmitting && (e.key === "Enter" || ((e.metaKey || e.ctrlKey) && e.key === "Enter"))) {
                e.preventDefault();
                onReplySubmit();
              }
            }}
          />
          <button
            type="button"
            className="btn primary sm"
            data-testid="thread-reply-submit"
            disabled={replySubmitting || !replyDraft.trim()}
            aria-busy={replySubmitting}
            onClick={onReplySubmit}
          >
            Reply
          </button>
          </div>
        </>
      )}
      renderReplyAction={(reply) => (
        <button
          type="button"
          className={`txt-btn like-btn ${reply.likeCount > 0 ? "has-likes" : ""} ${reply.likedByParticipant ? "liked" : ""}`}
          data-testid="reply-like-btn"
          aria-pressed={Boolean(reply.likedByParticipant)}
          onClick={() => onReplyLike(reply.id)}
        >
          <ThumbsUp weight={reply.likedByParticipant ? "fill" : "bold"} /> {reply.likeCount}
        </button>
      )}
    />
  );
}

function ComposerDock({
  value,
  onChange,
  placeholder,
  disabled,
  submitting,
  canSubmit,
  onSubmit,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  disabled: boolean;
  submitting: boolean;
  canSubmit: boolean;
  onSubmit: (e: FormEvent | React.MouseEvent | React.KeyboardEvent) => Promise<void> | void;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    ta.style.height = "auto";
    const max = 140;
    ta.style.height = `${Math.min(ta.scrollHeight, max)}px`;
  }, [value]);

  return (
    <div className="composer-dock" data-testid="participant-composer" aria-busy={submitting}>
      <textarea
        ref={ref}
        name="post"
        aria-label="Post a message"
        data-testid="participant-post-input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canSubmit) {
            e.preventDefault();
            void onSubmit(e);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
      />
      <button
        className="btn primary"
        data-testid="participant-post-submit"
        disabled={!canSubmit}
        aria-busy={submitting}
        onClick={(e) => void onSubmit(e)}
      >
        <PaperPlaneTilt weight="fill" /> Post
      </button>
    </div>
  );
}

function snippet(text: string, max = 90): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max).trimEnd()}…`;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Participant continuity still works in memory when storage is unavailable.
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Participant continuity still works in memory when storage is unavailable.
  }
}

function clientKeyStorage(sessionId: string): string {
  return `${STORAGE_PREFIX}.${sessionId}.clientKey`;
}

function aliasStorage(sessionId: string): string {
  return `${STORAGE_PREFIX}.${sessionId}.alias`;
}

function createClientKey(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}
